const isValidURL = url => {
  try {
    return !!new URL(url)
  } catch {
    return false
  }
}

export const extractQueryParams = (queries = {}, search = '') => {
  const queryParamsObject = Object.fromEntries(
    new URLSearchParams(search).entries()
  )
  const paramsPayload = {}

  for (const queryKey in queries) {
    if (Object.prototype.hasOwnProperty.call(queries, queryKey)) {
      const query = queries[queryKey]
      const paramName = query.value
      const datasourceId = query.datasourceId

      if (paramName && queryParamsObject[paramName]) {
        const compositeKey = `${datasourceId}.${queryKey}`
        paramsPayload[compositeKey] = queryParamsObject[paramName]
      }
    }
  }

  return paramsPayload
}

export const getNativeAppURL = (bundleId, alias, componentId, paramsPayload) =>
  `${bundleId}://${alias}?screen=${componentId}&params=${encodeURIComponent(
    JSON.stringify(paramsPayload)
  )}`

/**
 * Where the second button sends the user: the maker's custom fallback URL for
 * the current platform (Custom Redirect, usually a store link), or the web app
 * opened on the deep linked screen (Redirect to PWA).
 * `url` is null when there is nowhere to go (invalid custom URL, or the page
 * already shows the deep linked screen). `isCustomFallback` is true only for a
 * valid custom URL.
 */
export const getBrowserURL = ({
  deepLinking = {},
  agent,
  componentId,
  paramsPayload,
  href,
}) => {
  const { androidFallbackURL, iosFallbackURL, fallbackToPWA } = deepLinking

  if (!fallbackToPWA) {
    const fallbackURL =
      agent === 'android'
        ? androidFallbackURL
        : agent === 'ios'
        ? iosFallbackURL
        : null

    if (fallbackURL) {
      if (!isValidURL(fallbackURL)) {
        console.error('Invalid fallback URL:', fallbackURL)

        return { url: null, isCustomFallback: false }
      }

      return { url: fallbackURL, isCustomFallback: true }
    }
  }

  const currentUrl = new URL(href)
  const params = currentUrl.searchParams

  if (Object.keys(paramsPayload).length > 0) {
    params.set('params', JSON.stringify(paramsPayload))
  }

  if (params.get('target') === componentId) {
    return { url: null, isCustomFallback: false }
  }

  params.set('target', componentId)

  return {
    url: `${currentUrl.origin}${currentUrl.pathname}?${params.toString()}`,
    isCustomFallback: false,
  }
}

/**
 * Resolves a deep link alias into the URLs the Share page can send the user
 * to. Returns null when the alias is not a configured deep link.
 */
export const resolveDeepLink = ({ app, alias, agent, href }) => {
  const deepLinkConfig = app?.deepLinking?.aliases?.[alias]

  if (!deepLinkConfig) {
    console.warn(`No deep link configuration found for alias "${alias}"`)

    return null
  }

  const { componentId, query: queries } = deepLinkConfig

  if (!componentId) {
    console.warn(`No componentId found for alias "${alias}"`)

    return null
  }

  const paramsPayload = extractQueryParams(queries, new URL(href).search)

  const bundleIdMap = {
    android: app?.androidSettings?.bundleId,
    ios: app?.iosSettings?.bundleId,
  }
  const bundleId = bundleIdMap[agent]

  const { url: browserURL, isCustomFallback } = getBrowserURL({
    deepLinking: app.deepLinking,
    agent,
    componentId,
    paramsPayload,
    href,
  })

  // Already on the deep linked screen in the browser (the user picked
  // "Continue in Browser"): don't ask again.
  const alreadyInBrowser =
    !browserURL && new URL(href).searchParams.get('target') === componentId

  const nativeAppURL =
    bundleId && !alreadyInBrowser
      ? getNativeAppURL(bundleId, alias, componentId, paramsPayload)
      : null

  return { nativeAppURL, browserURL, isCustomFallback }
}
